import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useUser } from "../context/UserContext";
import { useNotification } from "../context/NotificationContext";

function AccessPanel({ darkMode, onClose }) {
    const { currentUser } = useUser();
    const { showAlert } = useNotification();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");


    const isGlobalAdmin = currentUser?.role === "Global Admin";
    const isOperationalAdmin = currentUser?.role === "Operational Admin";

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from("profiles")
                .select("*")
                .order("name", { ascending: true });
            if (error) throw error;
            console.log("Access Panel Users Data:", data);

            // Filter: Do not show Global Admins or Operational Admins
            const filteredUsers = (data || []).filter(u => u.role !== 'Global Admin' && u.role !== 'Operational Admin');
            setUsers(filteredUsers);
            if (data && data.length > 0) {
                const sample = data[0];
                if (sample.can_manage_users === undefined) {
                    showAlert("WARNING: Database migration pending. Please run the SQL script.");
                    console.warn("Missing permission columns in profiles table.");
                }
            }
        } catch (err) {
            console.error("Error fetching users for access panel:", err);
            showAlert("Failed to load users");
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleTogglePermission = async (userId, field, currentValue) => {
        // Find the target user
        const targetUser = users.find(u => u.id === userId);
        if (!targetUser) return;

        // Security check: Operational Admin cannot modify Global Admin permissions
        if (isOperationalAdmin && targetUser.role === "Global Admin") {
            showAlert("Security Restriction: Operational Admins cannot modify Global Admin permissions.");
            return;
        }

        // Operational Admin cannot modify other Operational Admins (except themselves)
        if (isOperationalAdmin && targetUser.role === "Operational Admin" && targetUser.id !== currentUser.id) {
            showAlert("Security Restriction: Operational Admins cannot modify other Operational Admin permissions.");
            return;
        }

        try {
            setUpdating(`${userId}-${field}`);
            const newValue = !currentValue;

            const { error } = await supabase
                .from("profiles")
                .update({ [field]: newValue })
                .eq("id", userId);

            if (error) throw error;

            setUsers(prev => prev.map(u => u.id === userId ? { ...u, [field]: newValue } : u));
            showAlert(`Permission updated successfully!`);
        } catch (err) {
            console.error(`Error updating ${field}:`, err);
            showAlert("Failed to update permission");
        } finally {
            setUpdating(null);
        }
    };

    if (!isGlobalAdmin && !isOperationalAdmin) {
        return (
            <div className="p-8 text-center text-red-500 font-bold">
                Access Denied. Only Admins can access this panel.
            </div>
        );
    }

    return (
        <div style={{ padding: 0, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
            <div className={`p-6 border-b flex items-center justify-between ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <div>
                    <h2 className="text-xl font-bold">Access Panel</h2>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Manage granular administrative permissions</p>
                </div>
                {/* Search Input */}
                <div className={`relative w-64 rounded-xl flex items-center px-4 py-2 border ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>

                    <input id="searchUsers" name="searchUsers"
                        type="text"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`bg-transparent border-none outline-none w-full text-sm font-medium ${darkMode ? 'text-gray-200 placeholder-gray-500' : 'text-gray-700 placeholder-gray-400'}`}
                    />
                </div>
            </div>

            {/* Added min-h-0 to ensure scrolling works within flex container */}
            <div style={{ flex: 1, overflowY: "auto", padding: "24px", minHeight: 0 }}>
                {loading ? (
                    <div className="flex items-center justify-center p-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-700">
                        <table className="w-full text-left border-collapse" style={{ minWidth: "600px", tableLayout: "fixed" }}>
                            <thead className={`${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                                <tr>
                                    <th className="p-4 text-xs font-bold uppercase tracking-wider" style={{ width: "50%" }}>User</th>
                                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-center" style={{ width: "25%" }}>User Management</th>
                                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-center" style={{ width: "25%" }}>Team Management</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {users.filter(u => u.name.toLowerCase().includes(searchQuery.toLowerCase())).map(user => (
                                    <tr key={user.id} className={`${darkMode ? 'hover:bg-gray-800/30' : 'hover:bg-gray-50/50'} transition-all`}>
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center font-bold text-brand text-xs">
                                                    {user.photo_url ? (
                                                        <img src={user.photo_url} alt="" className="w-full h-full rounded-full object-cover" />
                                                    ) : (
                                                        user.name.charAt(0).toUpperCase()
                                                    )}
                                                </div>
                                                <div className="flex flex-col">
                                                    <div className={`font-bold text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{user.name}</div>
                                                    <div className={`text-[10px] uppercase tracking-tighter ${darkMode ? 'text-gray-400 opacity-80' : 'text-gray-500 opacity-60'}`}>{user.role}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 text-center">
                                            <button
                                                onClick={() => handleTogglePermission(user.id, 'can_manage_users', user.can_manage_users)}
                                                disabled={updating === `${user.id}-can_manage_users`}
                                                style={{
                                                    backgroundColor: (user.can_manage_users || false) ? '#76AB3F' : '#e5e7eb', // Explicit colors
                                                    width: '44px',
                                                    height: '24px',
                                                    borderRadius: '9999px',
                                                    position: 'relative',
                                                    border: 'none',
                                                    cursor: updating === `${user.id}-can_manage_users` ? 'not-allowed' : 'pointer',
                                                    opacity: updating === `${user.id}-can_manage_users` ? 0.5 : 1,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    transition: 'background-color 0.2s'
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        width: '16px',
                                                        height: '16px',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'white',
                                                        transition: 'transform 0.2s',
                                                        transform: (user.can_manage_users || false) ? 'translateX(24px)' : 'translateX(4px)'
                                                    }}
                                                />
                                            </button>
                                        </td>
                                        <td className="p-4 text-center">
                                            <button
                                                onClick={() => handleTogglePermission(user.id, 'can_manage_teams', user.can_manage_teams)}
                                                disabled={updating === `${user.id}-can_manage_teams`}
                                                style={{
                                                    backgroundColor: (user.can_manage_teams || false) ? '#76AB3F' : '#e5e7eb',
                                                    width: '44px',
                                                    height: '24px',
                                                    borderRadius: '9999px',
                                                    position: 'relative',
                                                    border: 'none',
                                                    cursor: updating === `${user.id}-can_manage_teams` ? 'not-allowed' : 'pointer',
                                                    opacity: updating === `${user.id}-can_manage_teams` ? 0.5 : 1,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    transition: 'background-color 0.2s'
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        width: '16px',
                                                        height: '16px',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'white',
                                                        transition: 'transform 0.2s',
                                                        transform: (user.can_manage_teams || false) ? 'translateX(24px)' : 'translateX(4px)'
                                                    }}
                                                />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AccessPanel;
